import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Link,
  Stack,
} from "@mui/material";
import { FE_VERSION, FE_BUILD_DATE } from "../../constants/version";
import { API_CONSTANTS } from "../../constants/apiConstants";
import { UserService } from "../../services/UserService";
import { useThemeMode } from "../../theme/ThemeContext";

const AboutUs = () => {
  const theme = useTheme();
  const { isUta } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [beVersion, setBeVersion] = useState("…");
  const [beDeployTime, setBeDeployTime] = useState(null);
  const isLoggedIn = UserService.isLoggedIn && !UserService.isUserTokenExpired;

  useEffect(() => {
    fetch(`${API_CONSTANTS.BASE_URL}${API_CONSTANTS.GET_VERSION}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setBeVersion(d.version || "unknown");
        setBeDeployTime(d.deploy_time || null);
      })
      .catch(() => setBeVersion("unavailable"));
  }, []);

  return (
    <Container maxWidth="md" sx={{ mt: 10, mb: 6 }}>
      <Box textAlign="center" mb={2}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
          Academic-Scholars
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Recognizing Academic Excellence in STEM, Medicine, Liberal Arts, and
          Business
        </Typography>
      </Box>

      <Card elevation={3} sx={{ borderRadius: "6px", mb: 5 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            About
          </Typography>
          <Stack spacing={2}>
            <Typography variant="body1">
              In 2006, the Society for Women's Health Research in Washington DC
              initiated a project to identify STEM awards given to academic
              scholars. It was observed that women were not proportionately
              represented among award recipients. This led to the creation of
              the Raise Project, which collected award data from the past 25
              years and built a system to track gender representation.
            </Typography>
            <Typography variant="body1">
              After 2014, Florence Haseltine-founder of the Society and retired
              NIH Center Director-took over the project. In 2019, it was moved
              to the University of Texas at Arlington, where the data scope
              expanded to include liberal arts and business. The database now
              holds nearly 400,000 cited awards
              {isUta ? " and emphasizes UTA faculty accomplishments." : "."}
            </Typography>
            <Typography variant="body1">
              Users can explore awards by recipient, year, or society. Gender
              distribution for each award is shown when selected.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={3} sx={{ borderRadius: "6px", mb: 5 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Methodology
          </Typography>
          <Stack spacing={2}>
            <Typography variant="body1">
              Data is gathered by annually scraping websites of scholarly
              societies and professional organizations, including those listed
              by the National Research Council. Over 3,000 awards are tracked,
              updated, and classified in a content management system.
            </Typography>
            <Typography variant="body1">
              Parsed data includes award name, year, recipient, awarding body,
              description, and due date. Recipients with multiple awards are
              linked accordingly. Gender is inferred when possible through name
              or photograph; ethnic identity is not categorized.
            </Typography>
            <Typography variant="body1">
              Award data spans from 1981 to present. Gender representation in
              specific fields is sourced from NSF and AAMC publications.
            </Typography>
            <Typography variant="body1">
              Visit the project at{" "}
              <Link
                href="https://academic-scholar.org"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                academic-scholar.org
              </Link>
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={3} sx={{ borderRadius: "6px" }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Publications
          </Typography>
          <Stack spacing={2}>
            <Typography variant="body2">
              Lincoln A, Pincus S, Koster J, and Leboy P. The Matilda Effect in
              science: Awards and prizes in the US, 1990s and 2000s. <br />
              <i>Soc Stud Sci</i>: 42(2) 307–320, 2012
            </Typography>
            <Typography variant="body2">
              Lincoln A, Pincus S, and Leboy P. Scholars' awards go mainly to
              men. <br />
              <i>Nature</i> 469:472, 2011
            </Typography>
            <Typography variant="body2">
              Lincoln A, Pincus S, and Schick V. Evaluating Science or
              Evaluating Gender? <br />
              <i>The Back Page, APS News</i> 18(6):8, 2009
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {isLoggedIn && (
        <Card elevation={3} sx={{ borderRadius: "6px", mt: 5 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Version
            </Typography>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              divider={!isMobile && <Divider orientation="vertical" flexItem />}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Frontend
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {FE_VERSION}
                </Typography>
                {FE_BUILD_DATE && (
                  <Typography variant="caption" color="text.secondary">
                    Deployed {new Date(FE_BUILD_DATE).toLocaleString()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Backend
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {beVersion}
                </Typography>
                {beDeployTime && (
                  <Typography variant="caption" color="text.secondary">
                    Deployed {new Date(beDeployTime).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default AboutUs;
